import React, { useMemo, useState } from 'react';

/**
 * KundliChart - SVG-based North Indian Kundli chart matching drikpanchang.com style
 */
const KundliChart = ({ chartData, title = 'Kundli Chart' }) => {
  const [hoveredPlanet, setHoveredPlanet] = useState(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1000);
  const SVG_WIDTH = 400;
  const SVG_HEIGHT = 266.67;

  // Handle window resize
  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Planet color mapping matching standard Vedic astrology
  // Extended to support all transliterations and variations from D1-D60
  const PLANET_COLORS = {
    // Sun
    surya: '#FF4500', sun: '#FF4500', 'sun (surya)': '#FF4500', 'सूर्य': '#FF4500',
    // Moon
    chandra: '#FFFFFF', moon: '#FFFFFF', 'moon (chandra)': '#FFFFFF', 'चंद्र': '#FFFFFF',
    // Mars
    mangal: '#BB0000', mars: '#BB0000', 'mars (mangal)': '#BB0000', 'मंगल': '#BB0000',
    // Mercury
    budha: '#7B0000', mercury: '#7B0000', 'mercury (budha)': '#7B0000', 'बुध': '#7B0000',
    // Jupiter
    brihaspati: '#A52583', jupiter: '#A52583', 'jupiter (brihaspati)': '#A52583', 'बृहस्पति': '#A52583', 'guru': '#A52583',
    // Venus
    shukra: '#00AA00', venus: '#00AA00', 'venus (shukra)': '#00AA00', 'शुक्र': '#00AA00',
    // Saturn
    shani: '#4B4B4B', saturn: '#4B4B4B', 'saturn (shani)': '#4B4B4B', 'शनि': '#4B4B4B',
    // Rahu
    rahu: '#000000', 'rahu (north node)': '#000000', 'north node': '#000000', 'राहु': '#000000',
    // Ketu
    ketu: '#8B4513', 'ketu (south node)': '#8B4513', 'south node': '#8B4513', 'केतु': '#8B4513',
    // Ascendant/Lagna
    asc: '#FFD700', lagna: '#FFD700', ascendant: '#FFD700', 'ascendant (lagna)': '#FFD700', 'लग्न': '#FFD700',
    // Modern planets
    uranus: '#007BFF', neptune: '#730084', pluto: '#333333',
    // Different case variations
    'sun': '#FF4500', 'moon': '#FFFFFF', 'mars': '#BB0000', 'mercury': '#7B0000',
    'jupiter': '#A52583', 'venus': '#00AA00', 'saturn': '#4B4B4B'
  };

  const SHORT_NAMES = {
    'Ascendant': 'Asc', 'Ascendant (Lagna)': 'Asc', 'Lagna': 'Asc',
    'Sun': 'Su', 'Moon': 'Mo', 'Mars': 'Mar', 'Mercury': 'Me',
    'Jupiter': 'Jup', 'Venus': 'Ve', 'Saturn': 'Sat', 'Rahu': 'Ra', 'Ketu': 'Ketu',
    'Uranus': 'Ura', 'Neptune': 'Nep', 'Pluto': 'Plu'
  };

  // Extract planet positions from graha_table
  const planetsByHouse = useMemo(() => {
    const grouped = {};
    for (let i = 1; i <= 12; i++) grouped[i] = [];

    if (!chartData?.graha_table || !Array.isArray(chartData.graha_table)) {
      return grouped;
    }

    chartData.graha_table.forEach((graha) => {
      const name = graha.graha || graha.Graha || graha.planet || graha.name || 'Unknown';
      
      let house = 1;
      if (graha.house) house = parseInt(graha.house) || 1;
      else if (graha.isIn) house = parseInt(graha.isIn) || 1;
      else if (graha['Is In']) house = parseInt(graha['Is In']) || 1;
      else if (graha.bhava) house = parseInt(graha.bhava) || 1;
      else if (graha.currentHouse) house = parseInt(graha.currentHouse) || 1;
      
      house = Math.max(1, Math.min(12, house));

      // Normalize name for color lookup: lowercase + remove extra info in parentheses
      let key = name.toLowerCase().trim();
      // Strip everything after parentheses for lookups
      key = key.split('(')[0].trim();
      
      // Try exact match first, then try without parenthetical info
      let color = PLANET_COLORS[key] || PLANET_COLORS[name.toLowerCase()] || '#999999';
      
      // If still gray, try to match with common planet names more flexibly
      if (color === '#999999') {
        const planetMap = {
          'sun': '#FF4500', 'surya': '#FF4500',
          'moon': '#FFFFFF', 'chandra': '#FFFFFF',
          'mars': '#BB0000', 'mangal': '#BB0000',
          'mercury': '#7B0000', 'budha': '#7B0000',
          'jupiter': '#A52583', 'brihaspati': '#A52583', 'guru': '#A52583',
          'venus': '#00AA00', 'shukra': '#00AA00',
          'saturn': '#4B4B4B', 'shani': '#4B4B4B',
          'rahu': '#000000', 'north node': '#000000',
          'ketu': '#8B4513', 'south node': '#8B4513',
          'ascendant': '#FFD700', 'lagna': '#FFD700', 'asc': '#FFD700'
        };
        
        // Try to match just the first word
        const firstWord = key.split(/[\s\-_]/)[0];
        color = planetMap[firstWord] || '#999999';
      }
      
      const shortName = SHORT_NAMES[name] || name.substring(0, 3).toUpperCase();

      grouped[house].push({ name, shortName, color });
    });

    return grouped;
  }, [chartData]);

  // Planet positions for each house (multiple positions per house for dynamic planet placement)
  // Positions are distributed throughout each house area in the drikpanchang layout
  const planetPositions = {
    1: [{ x: 270, y: 35 }, { x: 290, y: 50 }, { x: 310, y: 35 }, { x: 280, y: 60 }, { x: 300, y: 65 }],
    2: [{ x: 180, y: 85 }, { x: 210, y: 95 }, { x: 190, y: 110 }, { x: 220, y: 105 }, { x: 200, y: 120 }],
    3: [{ x: 80, y: 35 }, { x: 110, y: 50 }, { x: 90, y: 65 }, { x: 120, y: 45 }, { x: 100, y: 30 }],
    4: [{ x: 50, y: 60 }, { x: 70, y: 85 }, { x: 60, y: 100 }, { x: 80, y: 75 }, { x: 65, y: 45 }],
    5: [{ x: 100, y: 180 }, { x: 80, y: 200 }, { x: 120, y: 210 }, { x: 110, y: 190 }, { x: 90, y: 220 }],
    6: [{ x: 50, y: 170 }, { x: 70, y: 185 }, { x: 60, y: 210 }, { x: 80, y: 195 }, { x: 55, y: 150 }],
    7: [{ x: 100, y: 225 }, { x: 80, y: 240 }, { x: 120, y: 235 }, { x: 110, y: 250 }, { x: 90, y: 215 }],
    8: [{ x: 180, y: 190 }, { x: 210, y: 180 }, { x: 190, y: 165 }, { x: 220, y: 175 }, { x: 200, y: 155 }],
    9: [{ x: 270, y: 225 }, { x: 290, y: 210 }, { x: 310, y: 220 }, { x: 280, y: 200 }, { x: 300, y: 215 }],
    10: [{ x: 330, y: 170 }, { x: 350, y: 185 }, { x: 340, y: 200 }, { x: 360, y: 175 }, { x: 345, y: 155 }],
    11: [{ x: 270, y: 110 }, { x: 250, y: 130 }, { x: 280, y: 120 }, { x: 260, y: 100 }, { x: 290, y: 135 }],
    12: [{ x: 330, y: 85 }, { x: 350, y: 60 }, { x: 340, y: 75 }, { x: 360, y: 50 }, { x: 355, y: 95 }]
  };

  const getPositionForPlanet = (houseNum, planetIdx) => {
    const positions = planetPositions[houseNum] || [];
    return positions[planetIdx] || { x: 200, y: 133.33 };
  };

  return (
    <div style={{ marginTop: '20px', marginBottom: '20px' }}>
      <h3 style={{ color: '#ffd700', marginBottom: '12px', fontSize: '16px' }}>🪐 {title}</h3>
      
        <svg
          viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
          width="500"
          height={Math.max(170, Math.min(350, Math.round(170 + (windowWidth - 300) * 0.3)))}
          xmlns="http://www.w3.org/2000/svg"
          style={{ maxWidth: '100%'}}
        >
          {/* Background */}
          <rect x="0" y="0" width={SVG_WIDTH} height={SVG_HEIGHT} fill="#EFB861" stroke="#B80000" strokeWidth="2" />

          {/* Center reference lines */}
          <line stroke="#B80000" x1="190" y1="133.33" x2="210" y2="133.33" />
          <line stroke="#B80000" x1="200" y1="123.33" x2="200" y2="143.33" />
          <line stroke="#B80000" x1="190" y1="133.33" x2="190" y2="123.33" />
          <line stroke="#B80000" x1="200" y1="143.33" x2="190" y2="143.33" />
          <line stroke="#B80000" x1="210" y1="133.33" x2="210" y2="143.33" />
          <line stroke="#B80000" x1="200" y1="123.33" x2="210" y2="123.33" />

          {/* 12 House sections - top section */}
          <path fill="none" stroke="#B80000" d="M 200,0 Q 200,36.3636 241.665,38.095 Q 283.33,39.8264 283.33,76.19 L 210 123.33 L 200 123.33 L 200 133.33 L 190 133.33 L 190 123.33 L 116.67 76.19 Q 116.67,39.8264 158.335,38.095 Q 200,36.3636 200,0 z" />
          
          {/* Right section */}
          <path fill="none" stroke="#B80000" d="M 283.33 76.19 Q 319.6936,76.19 341.665,104.76 Q 363.6364,133.33 400,133.33 Q 363.6364,133.33 341.665,162.665 Q 319.6936,192 283.33,192 L 210,143.33 L 210,133.33 L 200 133.33 L 200 123.33 L 210 123.33 z" />
          
          {/* Bottom section */}
          <path fill="none" stroke="#B80000" d="M 200,133.33 L 210,133.33 L 210,143.33 L 283.33,192 Q 283.33,228.3636 241.665,229.335 Q 200,230.3064 200,266.67 Q 200,230.3064 158.335,229.335 Q 116.67,228.3636 116.67,192 L 190,143.33 L 200,143.33 z" />
          
          {/* Left section */}
          <path fill="none" stroke="#B80000" d="M 200,133.33 L 200,143.33 L 190,143.33 L 116.67,192 Q 80.3064,192 58.335,162.665 Q 36.3636,133.33 0,133.33 Q 36.3636,133.33 58.335,104.76 Q 80.3064,76.19 116.67,76.19 L 190,123.33 L 190,133.33 z" />
          
          {/* Corner sections */}
          <path fill="none" stroke="#B80000" d="M 200,0 Q 200,36.3636 241.665,38.095 Q 283.33,39.8264 283.33,76.19 L 400, 0 z" />
          <path fill="none" stroke="#B80000" d="M 116.67,76.19 Q 116.67,39.8264 158.335,38.095 Q 200,36.3636 200,0 L 0, 0 z" />
          <path fill="none" stroke="#B80000" d="M 0,133.33 Q 36.3636,133.33 58.335,104.76 Q 80.3064,76.19 116.67,76.19 L 0, 0 z" />
          <path fill="none" stroke="#B80000" d="M 116.67,192 Q 80.3064,192 58.335,162.665 Q 36.3636,133.33 0,133.33 L 0, 266.67 z" />
          <path fill="none" stroke="#B80000" d="M 200,266.67 Q 200,230.3064 158.335,229.335 Q 116.67,228.3636 116.67,192 L 0, 266.67 z" />
          <path fill="none" stroke="#B80000" d="M 283.33,192 Q 283.33,228.3636 241.665,229.335 Q 200,230.3064 200,266.67 L 400, 266.67 z" />
          <path fill="none" stroke="#B80000" d="M 400,133.33 Q 363.6364,133.33 341.665,162.665 Q 319.6936,192 283.33,192 L 400, 266.67 z" />
          <path fill="none" stroke="#B80000" d="M 283.33,76.19 Q 319.6936,76.19 341.665,104.76 Q 363.6364,133.33 400,133.33 L 400, 0 z" />

          {/* HOUSE NUMBERS */}
          <text fill="#540000" x="287.78" y="45.08" fontSize="16" fontFamily="monospace" textAnchor="start" dominantBaseline="hanging">1</text>
          <text fill="#540000" x="195.56" y="102.22" fontSize="16" fontFamily="monospace" textAnchor="start" dominantBaseline="hanging">2</text>
          <text fill="#540000" x="98.89" y="45.08" fontSize="16" fontFamily="monospace" textAnchor="start" dominantBaseline="hanging">3</text>
          <text fill="#540000" x="68.89" y="58.41" fontSize="16" fontFamily="monospace" textAnchor="start" dominantBaseline="hanging">4</text>
          <text fill="#540000" x="160" y="124.44" fontSize="16" fontFamily="monospace" textAnchor="start" dominantBaseline="hanging">5</text>
          <text fill="#540000" x="68.89" y="192" fontSize="16" fontFamily="monospace" textAnchor="start" dominantBaseline="hanging">6</text>
          <text fill="#540000" x="96.67" y="209.78" fontSize="16" fontFamily="monospace" textAnchor="start" dominantBaseline="hanging">7</text>
          <text fill="#540000" x="195.56" y="146.67" fontSize="16" fontFamily="monospace" textAnchor="start" dominantBaseline="hanging">8</text>
          <text fill="#540000" x="287.78" y="209.78" fontSize="16" fontFamily="monospace" textAnchor="start" dominantBaseline="hanging">9</text>
          <text fill="#540000" x="313.33" y="191.11" fontSize="16" fontFamily="monospace" textAnchor="start" dominantBaseline="hanging">10</text>
          <text fill="#540000" x="226.67" y="124.44" fontSize="16" fontFamily="monospace" textAnchor="start" dominantBaseline="hanging">11</text>
          <text fill="#540000" x="315.56" y="56.19" fontSize="16" fontFamily="monospace" textAnchor="start" dominantBaseline="hanging">12</text>

          {/* PLANETS */}
          {(() => {
            const usedPositions = {}; // track used positions per house to avoid overlaps
            const houseLabelPositions = {
              1: { x: 287.78, y: 45.08 }, 2: { x: 195.56, y: 102.22 }, 3: { x: 98.89, y: 45.08 },
              4: { x: 68.89, y: 58.41 }, 5: { x: 160, y: 124.44 }, 6: { x: 68.89, y: 192 },
              7: { x: 96.67, y: 209.78 }, 8: { x: 195.56, y: 146.67 }, 9: { x: 287.78, y: 209.78 },
              10: { x: 313.33, y: 191.11 }, 11: { x: 226.67, y: 124.44 }, 12: { x: 315.56, y: 56.19 }
            };

            // approximate bounding boxes per house to keep planets inside sections
            const houseBounds = {
              1: { minX: 240, maxX: 360, minY: 10, maxY: 90 },
              2: { minX: 150, maxX: 240, minY: 60, maxY: 150 },
              3: { minX: 20, maxX: 140, minY: 10, maxY: 100 },
              4: { minX: 0, maxX: 120, minY: 40, maxY: 160 },
              5: { minX: 60, maxX: 180, minY: 100, maxY: 200 },
              6: { minX: 0, maxX: 120, minY: 150, maxY: 260 },
              7: { minX: 60, maxX: 180, minY: 200, maxY: 280 },
              8: { minX: 150, maxX: 240, minY: 140, maxY: 220 },
              9: { minX: 240, maxX: 360, minY: 190, maxY: 280 },
              10: { minX: 300, maxX: 400, minY: 140, maxY: 220 },
              11: { minX: 200, maxX: 300, minY: 90, maxY: 170 },
              12: { minX: 300, maxX: 380, minY: 30, maxY: 120 }
            };

            const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

            function findNonOverlapping(h, baseX, baseY, used) {
              const bounds = houseBounds[h] || { minX: 20, maxX: 380, minY: 10, maxY: 260 };
              const label = houseLabelPositions[h] || { x: 200, y: 133.33 };
              const minLabelDist = 20;
              const minPlanetDist = 18;

              let angle = 0;
              let radius = 0;
              let tries = 0;

              while (tries < 200) {
                const x = clamp(baseX + Math.cos(angle) * radius, bounds.minX + 6, bounds.maxX - 6);
                const y = clamp(baseY + Math.sin(angle) * radius, bounds.minY + 6, bounds.maxY - 6);

                // not too close to house label
                if (Math.hypot(x - label.x, y - label.y) < minLabelDist) {
                  angle += Math.PI / 4;
                  radius += 6;
                  tries++;
                  continue;
                }

                // not overlapping existing planets
                if (!used.some(p => Math.hypot(x - p.x, y - p.y) < minPlanetDist)) {
                  return { x, y };
                }

                angle += Math.PI / 4;
                radius += 6;
                tries++;
              }

              // fallback: base clamped
              return { x: clamp(baseX, bounds.minX + 6, bounds.maxX - 6), y: clamp(baseY, bounds.minY + 6, bounds.maxY - 6) };
            }

            return Object.entries(planetsByHouse).flatMap(([houseNum, planets]) =>
              planets.map((planet, idx) => {
                const h = parseInt(houseNum, 10);
                if (!usedPositions[h]) usedPositions[h] = [];

                // use base from predefined slots but fall back to house center
                const slots = planetPositions[h] && planetPositions[h].length ? planetPositions[h] : [houseLabelPositions[h] || { x: 200, y: 133.33 }];
                const base = slots[idx % slots.length] || (houseLabelPositions[h] || { x: 200, y: 133.33 });

                const pos = findNonOverlapping(h, base.x, base.y, usedPositions[h]);
                usedPositions[h].push(pos);

                const planetKey = `${houseNum}-${idx}`;
                const isHovered = hoveredPlanet === planetKey;

                return (
                  <g
                    key={`planet-${planetKey}`}
                    onMouseEnter={() => setHoveredPlanet(planetKey)}
                    onMouseLeave={() => setHoveredPlanet(null)}
                    style={{ cursor: 'pointer' }}
                  >
                    <text
                      x={pos.x-10}
                      y={pos.y-6}
                      fontSize="16"
                      fontFamily="monospace"
                      fill={planet.color}
                      fontWeight="normal"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{ opacity: isHovered ? 1 : 0.95 }}
                    >
                      {planet.shortName}
                    </text>
                  </g>
                );
              })
            );
          })()}

        </svg>
    </div>
  );
};

export default KundliChart;
